import React, { useState, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/react-hooks'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Search, LoaderCircle, ChevronDown, RotateCcw, Hash, Layers, Tag, MinusCircle, PencilRuler } from 'lucide-react'
import { cn } from '@/lib/utils'
import { confidenceLevelVariant } from '@/lib/status-variants'
import { Query } from '../../../../api'
import { useModuleActions } from '../../../../hooks/useMyPermissions'
import SortLabel from '../../../../components/grid/components/SortLabel'
import debounce from 'lodash/debounce'

interface ExtractedItem {
  id: string
  rowIndex: number
  code: string | null
  description: string
  unit: string | null
  quantity: number | null
  unitPrice: number | null
  totalPrice: number | null
  category: string | null
  level?: number
  rowType?: string | null
  matchedTenderCode?: string | null
  matchedTenderDescription?: string | null
  matchConfidence?: string | null
  matchSimilarity?: number | null
  mappingId?: string | null
  isApproved?: boolean | null
}

export interface RowCorrection {
  rowIndex: number
  itemId: string
  originalType: string
  newType: string
  description: string
}

interface Props {
  /** For tender items, pass tenderId. For bid items, pass bidId. */
  tenderId?: string
  bidId?: string
  /** Called when user has corrections and wants to re-extract */
  onCorrectionsSubmit?: (corrections: RowCorrection[]) => void
  /** Whether re-extraction is in progress */
  reExtracting?: boolean
  projectId?: string
}

const PAGE_SIZE = 1000

const ROW_TYPE_CONFIG: Record<string, { icon: React.ReactNode; className: string }> = {
  ITEM: { icon: <Hash className="h-3 w-3" />, className: 'border-blue-200 bg-blue-50 text-blue-700' },
  CATEGORY: { icon: <Layers className="h-3 w-3" />, className: 'border-purple-200 bg-purple-50 text-purple-700' },
  SUBTOTAL: { icon: <Tag className="h-3 w-3" />, className: 'border-amber-200 bg-amber-50 text-amber-700' },
  SKIP: { icon: <MinusCircle className="h-3 w-3" />, className: 'border-gray-200 bg-gray-100 text-gray-500' },
}

const formatNumber = (value: number | null | undefined) => {
  if (value == null) return <span className="text-muted-foreground">-</span>
  return value.toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const AiExtractedItemsTable: React.FC<Props> = ({ tenderId, bidId, onCorrectionsSubmit, reExtracting, projectId }) => {
  const { t } = useTranslation()
  const { canEdit } = useModuleActions('AI_EVALUATION', projectId)
  const [search, setSearch] = useState('')
  const [sorting, setSorting] = useState<SortingState>([])
  const [corrections, setCorrections] = useState<Map<string, RowCorrection>>(new Map())
  const [annotationMode, setAnnotationMode] = useState(false)
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set())

  const isTender = !!tenderId
  const isBid = !!bidId
  const queryDoc = isTender ? Query.AI_TENDER_ITEMS : Query.AI_BID_ITEMS
  const variables = isTender
    ? { tenderId, pageSize: PAGE_SIZE, search: search || undefined }
    : { bidId, pageSize: PAGE_SIZE }

  const { data, loading } = useQuery(queryDoc, {
    variables,
    skip: isTender ? !tenderId : !bidId,
    fetchPolicy: 'network-only',
  })

  const result = isTender ? data?.aiTenderItems : data?.aiBidItems
  const items: ExtractedItem[] = result?.items || []
  const total = result?.total || 0

  const onSearchDebounced = useCallback(
    debounce((value: string) => {
      setSearch(value)
    }, 200),
    [],
  )

  const handleRowTypeChange = useCallback((item: ExtractedItem, newType: string) => {
    const originalType = item.rowType || 'ITEM'
    if (newType === originalType) {
      // Revert: remove correction
      setCorrections(prev => {
        const next = new Map(prev)
        next.delete(item.id)
        return next
      })
    } else {
      setCorrections(prev => {
        const next = new Map(prev)
        next.set(item.id, {
          rowIndex: item.rowIndex,
          itemId: item.id,
          originalType,
          newType,
          description: item.description || '',
        })
        return next
      })
    }
  }, [])

  const getEffectiveType = useCallback((item: ExtractedItem): string => {
    return corrections.get(item.id)?.newType || item.rowType || 'ITEM'
  }, [corrections])

  const toggleCategory = useCallback((categoryId: string) => {
    setCollapsedCategories(prev => {
      const next = new Set(prev)
      if (next.has(categoryId)) next.delete(categoryId)
      else next.add(categoryId)
      return next
    })
  }, [])

  // Build a map: for each row, determine its parent group ID (CATEGORY or SUBTOTAL) for collapsing
  const parentGroupMap = useMemo(() => {
    const map = new Map<string, string>() // itemId → groupId
    let currentGroupId: string | null = null
    for (const item of items) {
      const type = getEffectiveType(item)
      if (type === 'CATEGORY' || type === 'SUBTOTAL') {
        currentGroupId = item.id
      } else if (currentGroupId) {
        map.set(item.id, currentGroupId)
      }
    }
    return map
  }, [items, getEffectiveType])

  // Count children per group for the badge
  const groupChildCount = useMemo(() => {
    const counts = new Map<string, number>()
    parentGroupMap.forEach((groupId) => {
      counts.set(groupId, (counts.get(groupId) || 0) + 1)
    })
    return counts
  }, [parentGroupMap])

  const columns = useMemo<ColumnDef<ExtractedItem>[]>(() => {
    const cols: ColumnDef<ExtractedItem>[] = [
      {
        id: 'rowNum',
        header: '#',
        size: 45,
        enableSorting: false,
        cell: ({ row }) => row.index + 1,
      },
      ...(annotationMode ? [{
        id: 'rowType',
        header: t('ai_type', 'Type'),
        size: 90,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const item = row.original as ExtractedItem
          const effectiveType = getEffectiveType(item)
          const isChanged = corrections.has(item.id)
          const config = ROW_TYPE_CONFIG[effectiveType] || ROW_TYPE_CONFIG.ITEM
          return (
            <Select value={effectiveType} onValueChange={(v) => handleRowTypeChange(item, v)}>
              <SelectTrigger className={cn('h-6 text-xs border-0 gap-1 px-1.5', config.className, isChanged && 'ring-1 ring-primary/40')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(ROW_TYPE_CONFIG).map(([type, cfg]) => (
                  <SelectItem key={type} value={type} className="text-xs">
                    <span className="flex items-center gap-1.5">{cfg.icon} {type}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )
        },
      } as ColumnDef<ExtractedItem>] : []),
      {
        accessorKey: 'code',
        header: t('ai_item_code'),
        size: 100,
        cell: ({ getValue }) => {
          const code = getValue<string | null>()
          return code ? (
            <Badge variant="outline" className="font-mono text-xs px-1.5 py-0">
              {code}
            </Badge>
          ) : (
            <span className="text-muted-foreground">-</span>
          )
        },
      },
      {
        accessorKey: 'description',
        header: t('ai_item_description'),
        enableSorting: false,
        cell: ({ getValue, row }) => {
          const level = row.original.level || 0
          const type = getEffectiveType(row.original)
          const isGroup = type === 'CATEGORY' || type === 'SUBTOTAL'
          const childCount = isGroup ? groupChildCount.get(row.original.id) || 0 : 0
          const isCollapsed = isGroup && collapsedCategories.has(row.original.id)

          if (isGroup && childCount > 0) {
            return (
              <button
                className="flex items-center gap-1 cursor-pointer hover:text-primary transition-colors w-full text-left"
                style={level > 0 ? { paddingLeft: level * 16 } : undefined}
                onClick={() => toggleCategory(row.original.id)}
              >
                <ChevronDown className={cn('h-3.5 w-3.5 shrink-0 transition-transform', isCollapsed && '-rotate-90')} />
                <span>{getValue<string>()}</span>
                <Badge variant="secondary" className="ml-1.5 text-[10px] px-1 py-0 font-normal">
                  {childCount}
                </Badge>
              </button>
            )
          }

          return (
            <div style={level > 0 ? { paddingLeft: (level * 16) + (isGroup ? 0 : 18) } : { paddingLeft: parentGroupMap.has(row.original.id) ? 18 : 0 }}>
              {getValue<string>()}
            </div>
          )
        },
      },
      {
        accessorKey: 'unit',
        header: t('ai_item_unit'),
        size: 60,
        cell: ({ getValue }) => getValue<string>() || '-',
      },
      {
        accessorKey: 'quantity',
        header: t('ai_item_qty'),
        size: 90,
        meta: { align: 'right' },
        cell: ({ getValue }) => formatNumber(getValue<number | null>()),
      },
      {
        accessorKey: 'unitPrice',
        header: t('ai_item_unit_price'),
        size: 100,
        meta: { align: 'right' },
        cell: ({ getValue }) => formatNumber(getValue<number | null>()),
      },
      {
        accessorKey: 'totalPrice',
        header: t('ai_item_total_price'),
        size: 110,
        meta: { align: 'right' },
        cell: ({ getValue }) => <span className="font-medium">{formatNumber(getValue<number | null>())}</span>,
      },
    ]

    // Add match columns for bid items
    if (isBid) {
      cols.push(
        {
          id: 'matchedTo',
          header: t('ai_matched_to'),
          size: 200,
          enableSorting: false,
          cell: ({ row }) => {
            const { matchedTenderCode, matchedTenderDescription } = row.original
            if (!matchedTenderCode && !matchedTenderDescription) {
              return <span className="text-muted-foreground">—</span>
            }
            return (
              <div className="text-xs">
                {matchedTenderCode && (
                  <Badge variant="outline" className="font-mono text-xs px-1.5 py-0 mr-1">
                    {matchedTenderCode}
                  </Badge>
                )}
                <span className="text-muted-foreground">
                  {matchedTenderDescription && matchedTenderDescription.length > 40
                    ? matchedTenderDescription.slice(0, 40) + '…'
                    : matchedTenderDescription}
                </span>
              </div>
            )
          },
        },
        {
          id: 'matchConfidence',
          header: t('ai_confidence'),
          size: 80,
          cell: ({ row }) => {
            const conf = row.original.matchConfidence
            if (!conf) return <span className="text-muted-foreground">—</span>
            return (
              <Badge variant={confidenceLevelVariant(conf)} className="font-medium whitespace-nowrap text-xs">
                {conf}
              </Badge>
            )
          },
        },
      )
    }

    return cols
  }, [t, isBid, annotationMode, corrections, getEffectiveType, handleRowTypeChange, collapsedCategories, groupChildCount, parentGroupMap, toggleCategory])

  const table = useReactTable({
    data: items,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  if (loading && items.length === 0) {
    return (
      <div className="flex items-center justify-center p-8 text-muted-foreground">
        <LoaderCircle className="h-5 w-5 animate-spin mr-2" />
        {t('loading')}
      </div>
    )
  }

  return (
    <div>
      {/* Action bar */}
      <div className="flex items-center gap-2 mb-4">
        {onCorrectionsSubmit && canEdit && (
          <Button
            variant={annotationMode ? 'default' : 'outline'}
            size="sm"
            className="h-8 gap-1.5"
            onClick={() => {
              if (annotationMode && corrections.size > 0) {
                // Exiting with unsaved corrections — discard them
                setCorrections(new Map())
              }
              setAnnotationMode(!annotationMode)
            }}
          >
            <PencilRuler className="h-3.5 w-3.5" />
            {t('ai_annotate', 'Annotate')}
          </Button>
        )}
        {isTender && (
          <div className="relative ml-auto max-w-sm">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t('ai_search_items')}
              onChange={(e) => onSearchDebounced(e.target.value)}
              className="pl-8 h-9"
            />
          </div>
        )}
      </div>

      {/* Table */}
      <div className="rounded-md border border-border overflow-hidden bg-card">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const meta = (header.column.columnDef.meta || {}) as { align?: string }
                  const canSort = header.column.getCanSort()
                  const sortDir = header.column.getIsSorted()
                  return (
                    <TableHead
                      key={header.id}
                      className={cn(meta.align === 'right' && 'text-right')}
                      style={header.getSize() !== 150 ? { width: header.getSize() } : undefined}
                    >
                      {header.isPlaceholder ? null : canSort ? (
                        <SortLabel
                          column={{ title: flexRender(header.column.columnDef.header, header.getContext()) }}
                          direction={sortDir || undefined}
                          onSort={() => header.column.toggleSorting()}
                        />
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  {search ? t('ai_no_items_match') : t('ai_no_items_found')}
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => {
                const effectiveType = getEffectiveType(row.original)
                const isCategory = effectiveType === 'CATEGORY'
                const isSubtotal = effectiveType === 'SUBTOTAL'
                const isChanged = corrections.has(row.original.id)

                // Hide rows whose parent group is collapsed
                const parentGroupId = parentGroupMap.get(row.original.id)
                if (parentGroupId && collapsedCategories.has(parentGroupId)) return null

                return (
                  <TableRow
                    key={row.id}
                    className={cn(
                      isCategory && 'bg-muted/30 font-medium',
                      isSubtotal && 'bg-muted/20 italic text-muted-foreground',
                      isChanged && 'bg-primary/5',
                    )}
                  >
                    {row.getVisibleCells().map((cell) => {
                      const meta = (cell.column.columnDef.meta || {}) as { align?: string }
                      return (
                        <TableCell
                          key={cell.id}
                          className={cn(
                            meta.align === 'right' && 'text-right tabular-nums',
                          )}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      )
                    })}
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Corrections bar */}
      {annotationMode && onCorrectionsSubmit && corrections.size > 0 && (
        <div className="flex items-center justify-between gap-3 rounded-md border border-primary/30 bg-primary/5 px-4 py-2.5 mt-2">
          <div className="flex items-center gap-2 text-sm">
            <Badge variant="outline" className="border-primary/30 text-primary font-medium">
              {corrections.size}
            </Badge>
            <span className="text-muted-foreground">{t('ai_corrections_pending', 'row(s) changed')}</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCorrections(new Map())}
              disabled={reExtracting}
            >
              {t('ai_discard_corrections', 'Discard')}
            </Button>
            <Button
              size="sm"
              onClick={() => onCorrectionsSubmit(Array.from(corrections.values()))}
              disabled={reExtracting}
            >
              {reExtracting ? (
                <LoaderCircle className="h-4 w-4 animate-spin" />
              ) : (
                <RotateCcw className="h-4 w-4" />
              )}
              {t('ai_re_extract_with_corrections', 'Re-extract with corrections')}
            </Button>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center py-2 px-1">
        <div className="text-xs text-muted-foreground">
          {total} {t('Grid_row_count', 'row(s)')}
        </div>
      </div>
    </div>
  )
}

export default AiExtractedItemsTable
